import { Bell, Trophy, Wallet, AlertTriangle, CheckCircle, Users } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, limit, doc, writeBatch, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import AddIgnModal from '../components/AddIgnModal';
import LoadingOverlay from '../components/LoadingOverlay';

const isMock = import.meta.env.VITE_MOCK_AUTH === 'true';

interface Notification {
    id: string;
    type: 'tournament' | 'wallet' | 'system' | 'result' | 'squad_invite' | 'squad_update' | 'account_suspended' | 'account_reinstated' | 'penalty_applied' | 'rewards_distributed';
    title: string;
    message: string;
    time: string;
    read: boolean;
    // Squad invite fields
    squadId?: string;
    squadName?: string;
    game?: string;
    slotLabel?: string;
    inviterUsername?: string;
}

const initialMockNotifications: Notification[] = [
    { id: 'schedule-release', type: 'tournament', title: '📋 Match Schedule Released', message: 'Your match for Rift Legends Cup has been scheduled!\n📅 March 25, 2026 | ⏰ 2:00 PM (BST)\n🆚 Arcane Vanguard vs Phoenix Rising\nPlease review your match time and prepare accordingly. Good luck, Commander!', time: '1 hour ago', read: false },
    { id: '8', type: 'tournament', title: 'Rewards Distributed! 💰', message: 'Congratulations! Your rewards for Rift Clash Finals have been credited to your wallet. Check your balance now!', time: '1 min ago', read: false },
    { id: '9', type: 'tournament', title: 'Match Starting! 🎮', message: 'Your PUBG Showdown #42 match is about to start! Get your Room ID and Password from the tournament details to join the lobby.', time: '2 min ago', read: false },
    { id: '7', type: 'squad_invite', title: '🎮 Squad Invite!', message: 'NinjaStorm invited you to join "Team Liquid" for PUBG as Player 3.', time: 'Just now', read: false, squadId: 'SQ-mock-1', squadName: 'Team Liquid', game: 'PUBG', slotLabel: 'Player 3', inviterUsername: 'NinjaStorm' },
    { id: '1', type: 'tournament', title: 'Room ID Released', message: 'PUBG Showdown #42 room ID is now available. Join the match!', time: '5 min ago', read: false },
    { id: '2', type: 'wallet', title: 'Deposit Approved', message: 'Your T 500 deposit via bKash has been approved and added to your wallet.', time: '2 hours ago', read: false },
    { id: '3', type: 'result', title: '2nd Place! 🎉', message: 'You placed 2nd in Rift Clash Finals and earned T 800!', time: '12 hours ago', read: true },
    { id: '4', type: 'tournament', title: 'Tournament Starting Soon', message: 'FF Squad Battle starts in 6 hours. Make sure you\'re ready!', time: '14 hours ago', read: true },
    { id: '5', type: 'system', title: 'Welcome to TournaHub!', message: 'Your account was created successfully. Deposit credits to start playing.', time: '4 days ago', read: true },
    { id: '6', type: 'wallet', title: 'Withdrawal Processing', message: 'Your T 1,000 withdrawal to Nagad is being processed.', time: '1 day ago', read: true },
];

function getIcon(type: string) {
    switch (type) {
        case 'tournament': return <Trophy size={18} style={{ color: 'var(--accent-hover)' }} />;
        case 'wallet': return <Wallet size={18} style={{ color: 'var(--success)' }} />;
        case 'result': return <CheckCircle size={18} style={{ color: 'var(--warning)' }} />;
        case 'system': case 'account_reinstated': return <AlertTriangle size={18} style={{ color: 'var(--neon-blue)' }} />;
        case 'squad_invite': case 'squad_update': return <Users size={18} style={{ color: 'var(--warning)' }} />;
        case 'account_suspended': return <AlertTriangle size={18} style={{ color: 'var(--danger)' }} />;
        case 'penalty_applied': return <Wallet size={18} style={{ color: 'var(--warning)' }} />;
        case 'rewards_distributed': return <Trophy size={18} style={{ color: '#34d399' }} />;
        default: return <Bell size={18} />;
    }
}

export default function Notifications() {
    const { showConfirm, showAlert } = useModal();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mockNotifs, setMockNotifs] = useState<Notification[]>(initialMockNotifications);
    const [realNotifs, setRealNotifs] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(!isMock);
    
    // Smart IGN onboarding state
    const [ignModalOpen, setIgnModalOpen] = useState(false);
    const [ignModalGame, setIgnModalGame] = useState('');
    const [pendingAcceptNotifId, setPendingAcceptNotifId] = useState<string | null>(null);
    const [userIgns, setUserIgns] = useState<any[]>([]);
    const [globalLoadingText, setGlobalLoadingText] = useState<string | null>(null);

    useEffect(() => {
        if (!user?.uid || isMock) return;
        getDoc(doc(db, 'users', user.uid)).then(docSnap => {
            if (docSnap.exists()) setUserIgns(docSnap.data().igns || []);
        });
    }, [user?.uid, isMock]);

    useEffect(() => {
        if (isMock || !user?.uid) return;

        // Listen to THIS user's notifications subcollection only
        const q = query(
            collection(db, `users/${user.uid}/notifications`),
            orderBy('createdAt', 'desc'),
            limit(50),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data: Notification[] = [];
            snapshot.forEach((docSnap) => {
                const d = docSnap.data();
                let timeStr = 'Just now';
                if (d.createdAt?.toDate) {
                    timeStr = d.createdAt.toDate().toLocaleString();
                }
                data.push({
                    id: docSnap.id,
                    type: d.type === 'tournament_created' ? 'tournament' : d.type === 'rewards_distributed' ? 'rewards_distributed' : (d.type || 'system'),
                    title: d.title,
                    message: d.message,
                    time: timeStr,
                    read: d.read ?? false,
                    squadId: d.squadId,
                    squadName: d.squadName,
                    game: d.game,
                    slotLabel: d.slotLabel,
                    inviterUsername: d.inviterUsername
                });
            });
            setRealNotifs(data);
            setLoading(false);
        }, (err) => {
            console.error('Notifications listener error:', err);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [user?.uid]);

    // Mark all as read — works for both mock and live
    const handleMarkAllRead = async () => {
        if (isMock) {
            setMockNotifs(mockNotifs.map(n => ({ ...n, read: true })));
            window.dispatchEvent(new Event('mock_notifs_read'));
            return;
        }
        if (!user?.uid) return;
        // Update each unread notification in Firestore
        const unreadDocs = realNotifs.filter(n => !n.read);
        if (unreadDocs.length === 0) return;
        
        const batch = writeBatch(db);
        for (const n of unreadDocs) {
            batch.update(doc(db, `users/${user.uid}/notifications/${n.id}`), { read: true });
        }
        
        try {
            await batch.commit();
        } catch (e) {
            console.warn('Failed to mark notifications as read:', e);
        }
    };

    const dataSource = isMock ? mockNotifs : realNotifs;
    const unread = dataSource.filter(n => !n.read).length;

    return (
        <div className="page-wrapper" style={{ maxWidth: 720 }}>
            {globalLoadingText && <LoadingOverlay text={globalLoadingText} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div className="animate-in">
                    <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', marginBottom: 4 }}>🔔 Notifications</h1>
                    <p style={{ color: 'var(--text-muted)' }}>{unread > 0 ? `${unread} unread` : 'All caught up!'}</p>
                </div>
                {unread > 0 && <button className="btn btn-secondary btn-sm animate-in" onClick={handleMarkAllRead}>Mark all as read</button>}
            </div>

            {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading notifications...</div>}

            {dataSource.map((n, i) => (
                <div key={n.id} className={`card animate-in stagger-${(i % 6) + 1}`}
                    onClick={() => { if (n.type === 'rewards_distributed') navigate('/wallet'); }}
                    style={{
                        marginBottom: 8, display: 'flex', gap: 14, alignItems: 'flex-start',
                        cursor: n.type === 'rewards_distributed' ? 'pointer' : undefined,
                        background: !n.read
                            ? n.type === 'account_suspended' ? 'rgba(239,68,68,0.06)'
                            : n.type === 'penalty_applied' ? 'rgba(245,158,11,0.06)'
                            : 'rgba(99,102,241,0.04)'
                            : 'var(--bg-card)',
                        borderColor: !n.read
                            ? n.type === 'account_suspended' ? 'rgba(239,68,68,0.2)'
                            : n.type === 'penalty_applied' ? 'rgba(245,158,11,0.2)'
                            : 'rgba(99,102,241,0.15)'
                            : 'var(--border)',
                    }}
                >
                    <div style={{
                        width: 40, height: 40, borderRadius: 'var(--radius-md)', flexShrink: 0,
                        background: 'var(--bg-glass)', border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {getIcon(n.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{n.title}</span>
                            {!n.read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 6 }} />}
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 3, lineHeight: 1.5 }}>{n.message}</p>
                        {n.type === 'squad_invite' && !n.read && (
                            <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                                <button className="btn btn-primary btn-sm" onClick={() => {
                                    const game = n.game || 'PUBG';
                                    const hasIgn = userIgns.some((ign: any) => ign.game === game);
                                    
                                    if (!hasIgn) {
                                        // No IGN for this game → open AddIgnModal with locked game
                                        setIgnModalGame(game);
                                        setPendingAcceptNotifId(n.id);
                                        setIgnModalOpen(true);
                                    } else {
                                        // Has IGN → proceed with accept
                                        showConfirm(
                                            `Join "${n.squadName || 'this squad'}" as ${n.slotLabel || 'a member'}?`,
                                            async () => {
                                                setGlobalLoadingText(`Joining ${n.squadName || 'squad'}...`);
                                                try {
                                                    const functions = getFunctions();
                                                    const respond = httpsCallable(functions, 'respondToInvite');
                                                    await respond({ squadId: n.squadId, action: 'accept', ign: userIgns.find(i => i.game === game)?.name });
                                                    if (!isMock) await updateDoc(doc(db, `users/${user!.uid}/notifications/${n.id}`), { read: true });
                                                    showAlert(`Welcome to ${n.squadName}!`, '✅ Squad Joined');
                                                } catch (e: any) {
                                                    showAlert(e.message || "Failed to accept invite", "Error");
                                                } finally {
                                                    setGlobalLoadingText(null);
                                                }
                                            },
                                            undefined,
                                            'Join Squad'
                                        );
                                    }
                                }}>Accept</button>
                                <button className="btn btn-secondary btn-sm" onClick={() => {
                                    showConfirm(
                                        `Decline the invitation from ${n.inviterUsername || 'the leader'}?`,
                                        async () => {
                                            try {
                                                const functions = getFunctions();
                                                const respond = httpsCallable(functions, 'respondToInvite');
                                                await respond({ squadId: n.squadId, action: 'decline' });
                                                if (!isMock) await updateDoc(doc(db, `users/${user!.uid}/notifications/${n.id}`), { read: true });
                                                showAlert(`You declined the invitation to "${n.squadName}". The slot has been freed.`, 'Declined');
                                            } catch (e: any) {
                                                showAlert(e.message || "Failed to decline invite", "Error");
                                            }
                                        },
                                        undefined,
                                        'Decline Invite'
                                    );
                                }}>Decline</button>
                            </div>
                        )}
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6, display: 'block' }}>{n.time}</span>
                    </div>
                </div>
            ))}

            {/* Smart IGN Onboarding Modal — opens when user lacks IGN for the squad's game */}
            <AddIgnModal
                isOpen={ignModalOpen}
                onClose={() => { setIgnModalOpen(false); setPendingAcceptNotifId(null); }}
                lockedGame={ignModalGame}
                existingGames={userIgns.map((i: any) => i.game)}
                onSave={async (game, ignName, ignId) => {
                    // Save the IGN locally and to Firestore
                    const newIgns = [...userIgns, { game, name: ignName, idNumber: ignId }];
                    setUserIgns(newIgns);
                    if (user?.uid && !isMock) {
                        await updateDoc(doc(db, 'users', user.uid), { igns: newIgns });
                    }
                    setIgnModalOpen(false);

                    // Auto-accept the pending invite
                    if (pendingAcceptNotifId) {
                        const notif = dataSource.find(n => n.id === pendingAcceptNotifId);
                        if (notif) {
                            setGlobalLoadingText(`Joining ${notif.squadName || 'squad'}...`);
                            try {
                                const functions = getFunctions();
                                const respond = httpsCallable(functions, 'respondToInvite');
                                await respond({ squadId: notif.squadId, action: 'accept', ign: ignName });
                                if (!isMock) await updateDoc(doc(db, `users/${user!.uid}/notifications/${notif.id}`), { read: true });
                                showAlert(`IGN saved! You've joined "${notif.squadName}" for ${game}.`, '✅ Squad Joined');
                            } catch (e: any) {
                                showAlert(e.message || "Failed to join squad", "Error");
                            } finally {
                                setGlobalLoadingText(null);
                            }
                        }
                        setPendingAcceptNotifId(null);
                    }
                }}
            />
        </div>
    );
}
